import React, { FC, Fragment, useEffect, useLayoutEffect } from 'react'

import { History } from 'history'
import { Switch } from 'react-router'
import { SnackbarProvider } from 'notistack'
import { ConnectedRouter } from 'connected-react-router'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isoWeek from 'dayjs/plugin/isoWeek'
import isToday from 'dayjs/plugin/isToday'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import localeData from 'dayjs/plugin/localeData'
import relativeTime from 'dayjs/plugin/relativeTime'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

import Box from '@mui/material/Box'
import { CircularProgress } from '@mui/material'

import { useTypedSelector } from '@app/hooks/useTypedSelector'
import { selectUsersTimeLocale } from '@app/store/reducers/user/selectors'
import { useDefaultComponentTools } from '@app/hooks/useDefaultComponentTools'
import { TimeControllerProvider } from '@app/hooks/contexts/timeController/TimeControllerContext'

import {
	PAGE_DASHBOARD,
	PAGE_ERROR_401,
	PAGE_ERROR_50X,
	PAGE_LOGIN,
	PAGE_BAD_CONNECTION,
} from './constants/navigation'
import LoginPage from './pages/Login/LoginPage'
import Dashboard from './pages/Dashboard/Dashboard'
import ServerError from './pages/ServerError/ServerError'
import NotFoundRoot from './pages/NotFoundRoot/NotFoundRoot'
import PublicRoute from './components/Container/PublicRoute'
import PrivateRoute from './components/Container/PrivateRoute'
import NotAuthorized from './pages/NotAuthorized/NotAuthorized'
import BadConnection from './pages/BadConnection/BadConnection'
import { getLocaleData } from './store/reducers/locale/actions'
import { getSystemConfig } from './store/reducers/systemConfig/actions'
import { setViewportWidth } from './store/reducers/viewport/actions'

dayjs.extend(isToday)
dayjs.extend(localeData)
dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(isSameOrBefore)
dayjs.extend(isBetween)
dayjs.extend(isoWeek)
dayjs.extend(relativeTime)

interface AppProps {
	readonly history: History
}

const App: FC<AppProps> = ({ history }: AppProps) => {
	const { loading, setLoading, isMounted, alert, dispatch } =
		useDefaultComponentTools(true)
	const usersTimeLocale = useTypedSelector(selectUsersTimeLocale)

	useEffect(() => {
		let debounceTimeout: ReturnType<typeof setTimeout>

		const handleResizeWindow = (event: Event): void => {
			const target = event.target as Window

			clearTimeout(debounceTimeout)

			debounceTimeout = setTimeout(() => {
				dispatch(setViewportWidth(target.innerWidth))
			}, 200)
		}

		window.addEventListener('resize', handleResizeWindow)

		return () => window.removeEventListener('resize', handleResizeWindow)
	}, [dispatch])

	useLayoutEffect(() => {
		const systemConfiguring = async (): Promise<void> => {
			try {
				await dispatch(getSystemConfig())
				await dispatch(getLocaleData('eng'))
			} catch (e: any) {
				'message' in e && alert.error(e.message)
			} finally {
				isMounted() && setLoading(false)
			}
		}

		systemConfiguring()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<Fragment>
			{loading && (
				<Box
					display={'flex'}
					alignItems={'center'}
					justifyContent={'center'}
					position={'fixed'}
					top={0}
					right={0}
					bottom={0}
					left={0}
				>
					<CircularProgress />
				</Box>
			)}

			{!loading && (
				<TimeControllerProvider timeLocale={usersTimeLocale}>
					<SnackbarProvider maxSnack={3}>
						<ConnectedRouter history={history}>
							<Switch>
								<PublicRoute
									exact
									path={`${PAGE_LOGIN}*`}
									component={LoginPage}
								/>
								<PublicRoute
									exact
									path={PAGE_ERROR_401}
									component={NotAuthorized}
								/>
								<PublicRoute
									exact
									path={PAGE_ERROR_50X}
									component={ServerError}
								/>
								<PublicRoute
									exact
									path={PAGE_BAD_CONNECTION}
									component={BadConnection}
								/>

								<PrivateRoute
									exact
									path={`${PAGE_DASHBOARD}*`}
									component={Dashboard}
								/>
								<PublicRoute
									path={'*'}
									component={NotFoundRoot}
								/>
							</Switch>
						</ConnectedRouter>
					</SnackbarProvider>
				</TimeControllerProvider>
			)}
		</Fragment>
	)
}

export default App
